import { useParams } from 'react-router-dom';
import { useGetQuotation, useUpdateQuotation } from '@features/quotations/hooks/useQuotations';
import { App, Button, Card, Checkbox, Col, ConfigProvider, Flex, Form, Row, Space, Spin, Typography } from 'antd';
import { VehicleForm } from '@features/vehicles/components/VehicleForm';
import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { SUCCESS_COLOR } from '@shared/constants/colors';
import { ErrorMessage } from '@shared/components/ErrorMessage';
import { PERSON_ROLES, PersonForm } from '@shared/components/forms/PersonForm';
import { useUpdateVehicle } from '@features/vehicles/hooks/useVehicles';
import { useUpdateClient } from '@features/clients/hooks/useClients';
import { getErrorMessage } from '@shared/utils/errors/getErrorMessage';
import { useLoading } from '@shared/hooks/useLoading';
import { QuotationLoadingScreen } from 'Pages/QuotationPage/components/QuotationLoadingScreen';
import { useNavigate } from 'react-router-dom';

// TODO: Pendiente pasar las solicitudes a tanStack Query usando hooks y reemplazar Redux por Zustand para el manejo de estados
import { useDispatch, useSelector } from 'react-redux';
import { getQuotationPrice } from 'Pages/DataGatewayPage/routerForm/utils';
import { initializeExpectedPackagesResponsesAction, setPetitionAliveAction } from 'Redux/actions/comparationQuotationActions'
import { generateInsuranceQuotation } from 'Redux/actions/globalQuotation';
import { ROUTES } from '@/App/Router/routes';

const { Text } = Typography;

export const UpdateQuotationLayout = () => {
  const { id } = useParams();
  const { modal, notification } = App.useApp();
  const generateQuotationsByInsuranceCarrierLoading = useLoading();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedBranch = useSelector((state) => state.globals.selected_sede); // Se recupera la sede seleccionada en la app desde redux

  const [quotationContext, setQuotationContext] = useState({
    isBeneficiarySameAsTaker: true,
  });

  const [vehicleForm] = Form.useForm();
  const [takerForm] = Form.useForm();
  const [beneficiaryForm] = Form.useForm();

  const updateVehicleMutation = useUpdateVehicle();
  const updateClientMutation = useUpdateClient();
  const updateQuotationMutation = useUpdateQuotation();

  const isLoading = updateVehicleMutation.isPending || updateClientMutation.isPending || updateQuotationMutation.isPending || generateQuotationsByInsuranceCarrierLoading.isLoading;

  const { data, isFetching, isError, error } = useGetQuotation(id);
  const isDone = !!data?.number; // Indica si la cotización ya fue hecha, de lo contrario sigue pendiente por cotizar

  const updateQuotationContext = (newContext) => {
    setQuotationContext((prev) => ({ ...prev, ...newContext }));
  }

  const handleBeneficiarySameAsTakerChange = (value) => {
    updateQuotationContext({ isBeneficiarySameAsTaker: value });
  }

  const handleCancel = () => {
    // Se valida si hay cambios en alguno de los formularios
    const hasChanges = vehicleForm.isFieldsTouched() || takerForm.isFieldsTouched() || (!quotationContext.isBeneficiarySameAsTaker && beneficiaryForm.isFieldsTouched());

    if (!hasChanges) {
      navigate(-1);
      return;
    }

    modal.confirm({
      title: '¿Estás seguro?',
      content: 'Si cancelas, se perderán los cambios no guardados.',
      okText: 'Sí, estoy seguro',
      cancelText: 'Cancelar',
      width: '30em',
      onOk: () => navigate(-1),
    });
  }

  const handleUpdateAndQuote = async () => {
    const [newVehicle, newTaker, newBeneficiary] = await Promise.all([
      vehicleForm.validateFields(),
      takerForm.validateFields(),
      beneficiaryForm.validateFields(),
    ])

    try {
      await updateClientMutation.mutateAsync({ // Se actualiza el tomador de primero porque es más propenso a fallar por validaciones
        id: data.client.id,
        data: newTaker,
      });

      await updateVehicleMutation.mutateAsync({
        id: data.vehicle.id,
        data: newVehicle,
      });

      await updateQuotationMutation.mutateAsync({
        id,
        data: {
          isBeneficiarySameAsTaker: quotationContext.isBeneficiarySameAsTaker,
          beneficiary: newBeneficiary,
          branchId: !isDone ? selectedBranch.id : data.branchId, // Si la cotización ya fue hecha, se mantiene la sede original
        },
      });

      notification.success({
        message: <Text strong>Cotización actualizada</Text>,
        description: '¡La cotización se ha actualizado correctamente!',
      });
    } catch (error) {
      notification.error({
        message: <Text strong>Cotización no actualizada</Text>,
        description: getErrorMessage(error),
      });

      return;
    };

    // Se generan las cotizaciones por aseguradora
    generateQuotationsByInsuranceCarrierLoading.start();
    try {
      const price = await getQuotationPrice(isDone);
      const { insurance_carriers: quotedInsuranceCarriers } = await dispatch(generateInsuranceQuotation(id, price, isDone));
      dispatch(initializeExpectedPackagesResponsesAction(quotedInsuranceCarriers));
      dispatch(setPetitionAliveAction(true));
      // localStorage.setItem('consulting', true);
      navigate(ROUTES.QUOTATIONS.PLANS.LIST(id), { replace: true });
    } catch (error) {
      notification.error({
        message: 'No fue posible generar las cotizaciones por aseguradora',
        description: getErrorMessage(error),
      });
    } finally {
      generateQuotationsByInsuranceCarrierLoading.stop();
    };
  };

  // Se inicializa el valor de 'isBeneficiarySameAsTaker' cuando se carga la cotización
  useEffect(() => {
    if (data) {
      handleBeneficiarySameAsTakerChange(data.isBeneficiarySameAsTaker);
    }
  }, [data]);

  if (isFetching) {
    return (
      <Flex align='center' justify='center' gap='small' vertical style={{ height: '100%' }}>
        <Spin size='large' />
        <Text>Cargando...</Text>
      </Flex>
    );
  }

  if (isError) return <ErrorMessage error={error} />

  return (<>
    <Card title={isDone ? 'Actualizar cotización' : 'Revisar y cotizar'} styles={{ title: { textAlign: 'center' } }} loading={isFetching}>
      <Row gutter={[16, 16]}>
        <Col
          xs={{ span: 24 }}
          lg={{ flex: '1' }}
        >
          <VehicleForm
            form={vehicleForm}
            initialValues={data.vehicle}
            cardProps={{ title: 'Información del vehículo', type: 'inner', style: { height: '100%' } }}
          />
        </Col>
        <Col
          xs={{ span: 24 }}
          lg={{ flex: '1' }}
        >
          <Space size={24} direction='vertical'>
            <PersonForm
              form={takerForm}
              initialValues={data.client}
              role={PERSON_ROLES.TAKER}
              footer={
                <Checkbox
                  checked={quotationContext.isBeneficiarySameAsTaker}
                  onChange={(e) => handleBeneficiarySameAsTakerChange(e.target.checked)}
                >
                  El beneficiario es el mismo tomador
                </Checkbox>
              }
              cardProps={{ type: 'inner' }}
            />
            {!quotationContext.isBeneficiarySameAsTaker && <PersonForm form={beneficiaryForm} initialValues={data.beneficiary} role={PERSON_ROLES.BENEFICIARY} cardProps={{ type: 'inner' }} />}
          </Space>
        </Col>
        <Col span={24}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(18em, 18em))',
              placeContent: 'center',
              gap: 8,
            }}
          >
            <Button onClick={handleCancel}>Cancelar</Button>
            <ConfigProvider
              theme={{
                components: {
                  Button: {
                    colorPrimary: SUCCESS_COLOR,
                    algorithm: true,
                  },
                },
              }}
            >
              <Button
                type='primary'
                icon={<FontAwesomeIcon icon={faCheck} />}
                onClick={handleUpdateAndQuote}
              >
                {isDone ? 'Actualizar y generar cotizaciones' : 'Guardar y generar cotizaciones'}
              </Button>
            </ConfigProvider>
          </div>
        </Col>
      </Row>
    </Card>
    <QuotationLoadingScreen loading={isLoading} />
  </>
  );
};